import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, FileText, Settings, CheckCircle, Clock } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { RevisionSession, ChangeItem } from '@/types/resumeRevision';
import { ResumeRevisionService } from '@/services/resumeRevisionService';

// Step components (we'll create these next)
import { Step1ResumeSelection } from './Step1ResumeSelection';
import { Step2DocumentsAndNotes } from './Step2DocumentsAndNotes';
import { Step3ChangeItems } from './Step3ChangeItems';
import { Step4Preview } from './Step4Preview';
import { Step5AccuracyReport } from './Step5AccuracyReport';

interface ResumeRevisionsWizardProps {
  onComplete?: (session: RevisionSession) => void;
  onCancel?: () => void;
}

export const ResumeRevisionsWizard: React.FC<ResumeRevisionsWizardProps> = ({
  onComplete,
  onCancel
}) => {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [session, setSession] = useState<RevisionSession | null>(null);
  const [selectedResumeId, setSelectedResumeId] = useState<string>('');
  const [additionalDocuments, setAdditionalDocuments] = useState<Array<{
    filename: string;
    content: string;
    type: string;
  }>>([]);
  const [revisionNotes, setRevisionNotes] = useState('');

  const steps = [
    { id: 1, title: 'Select Resume', icon: FileText, description: 'Choose the resume to revise' },
    { id: 2, title: 'Documents & Notes', icon: Settings, description: 'Upload additional docs and add revision notes' },
    { id: 3, title: 'Review Changes', icon: AlertCircle, description: 'Review and approve AI-generated options' },
    { id: 4, title: 'Preview', icon: Clock, description: 'Preview the revised resume' },
    { id: 5, title: 'Final Report', icon: CheckCircle, description: 'Accuracy report and download' }
  ];

  const calculateProgress = () => {
    return ((currentStep - 1) / (steps.length - 1)) * 100;
  };

  const handleNext = async () => {
    if (currentStep === 1 && !selectedResumeId) {
      toast({
        title: "Resume Required",
        description: "Please select a resume to continue.",
        variant: "destructive"
      });
      return;
    }

    if (currentStep === 2) {
      // Create revision session and process documents
      setIsProcessing(true);
      try {
        // Create revision session
        const newSession = await ResumeRevisionService.createRevisionSession({
          resume_id: selectedResumeId,
          session_name: `Revision ${new Date().toLocaleDateString()}`,
          revision_notes: revisionNotes,
          additional_documents: additionalDocuments
        });
        
        setSession(newSession);
        
        toast({
          title: "Session Created",
          description: "Revision session created successfully.",
        });
      } catch (error) {
        console.error('Error creating session:', error);
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to create revision session.",
          variant: "destructive"
        });
        return;
      } finally {
        setIsProcessing(false);
      }
    }

    if (currentStep === 3 && session && !session.change_items?.length) {
      // Process revision instructions to generate change items
      setIsProcessing(true);
      try {
        await ResumeRevisionService.processRevisionInstructions(session.id);
        
        // Reload the session with change items
        const updatedSession = await ResumeRevisionService.getRevisionSession(session.id);
        if (updatedSession) {
          setSession(updatedSession);
        }
        
        toast({
          title: "Change Items Generated",
          description: "AI has analyzed your instructions and generated revision options.",
        });
      } catch (error) {
        console.error('Error processing instructions:', error);
        toast({
          title: "Error", 
          description: error instanceof Error ? error.message : "Failed to process revision instructions.",
          variant: "destructive"
        });
        return;
      } finally {
        setIsProcessing(false);
      }
    }

    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1ResumeSelection
            selectedResumeId={selectedResumeId}
            onResumeSelect={setSelectedResumeId}
          />
        );
      case 2:
        return (
          <Step2DocumentsAndNotes
            additionalDocuments={additionalDocuments}
            revisionNotes={revisionNotes}
            onDocumentsChange={setAdditionalDocuments}
            onNotesChange={setRevisionNotes}
          />
        );
      case 3:
        return session ? (
          <Step3ChangeItems
            session={session}
            onSessionUpdate={setSession}
          />
        ) : null;
      case 4:
        return session ? (
          <Step4Preview
            session={session}
          />
        ) : null;
      case 5:
        return session ? (
          <Step5AccuracyReport
            session={session}
            onComplete={onComplete}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-6 w-6" />
            Resume Revisions Wizard
          </CardTitle>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Update your resume with precision using AI-powered options and recommendations.
            </p>
            {session && (
              <Badge variant="outline">
                Session: {session.session_name}
              </Badge>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Progress Indicator */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Step {currentStep} of {steps.length}</span>
              <span className="text-sm text-muted-foreground">{Math.round(calculateProgress())}%</span>
            </div>
            <Progress value={calculateProgress()} className="w-full" />
            
            {/* Step indicators */}
            <div className="flex justify-between">
              {steps.map((step) => {
                const Icon = step.icon;
                const isCompleted = step.id < currentStep;
                const isCurrent = step.id === currentStep;
                
                return (
                  <div key={step.id} className="flex flex-col items-center space-y-2">
                    <div className={`
                      flex items-center justify-center w-10 h-10 rounded-full border-2
                      ${isCompleted ? 'bg-primary border-primary text-primary-foreground' : 
                        isCurrent ? 'border-primary text-primary' : 
                        'border-muted-foreground text-muted-foreground'}
                    `}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="text-center">
                      <div className={`text-xs font-medium ${isCurrent ? 'text-primary' : 'text-muted-foreground'}`}>
                        {step.title}
                      </div>
                      <div className="text-xs text-muted-foreground hidden md:block">
                        {step.description}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Step Content */}
      {renderCurrentStep()}

      {/* Navigation */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-between">
            <div className="space-x-2">
              {currentStep > 1 && (
                <Button variant="outline" onClick={handleBack}>
                  Back
                </Button>
              )}
              <Button variant="ghost" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
            
            <div className="space-x-2">
              {currentStep < steps.length ? (
                <Button 
                  onClick={handleNext} 
                  disabled={isProcessing}
                  className="min-w-24"
                >
                  {isProcessing ? 'Processing...' : 'Next'}
                </Button>
              ) : (
                <Button 
                  onClick={() => onComplete && session && onComplete(session)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Complete Revision
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};